import { useEffect, useMemo } from 'react';
import {
  GetTeamPermissions,
  getTeamPermissions,
} from '../../../../../utils/api/get-team-permissions/get-team-permissions';
import { ApolloClient } from '@apollo/client';
import { TeamPermissionName } from '../../../../../types/permissions';
import { usePermissions } from '../../../../../contexts/PermissionsContext';
import { Permissions } from '../../../../../shared/constants/permissions';

export type DocumentPermissions = {
  isAdmin: boolean;
  hasViewPermission: boolean;
  hasCommentPermission: boolean;
  hasEditPermission: boolean;
  hasInviteSendingPermission: boolean;
  hasApprovePermission: boolean;
  hasEditTemplatesPermission: boolean;
  hasManageTemplatesPermission: boolean;
};

type UseLeaseDocumentPermissions = {
  teamId?: string | null;
  apolloClient: ApolloClient<object>;
};

/**
 * Making sense of the team permissions payload with respect to the team.
 *
 * The following assumptions are made:
 *
 *   If your actual document permissions need to consider other factors, e.g. document states, etc.,
 *   these should be handled outside of the scope of this abstraction and in a separate concern.
 *
 * @param teamId
 * @param apolloClient
 */
export const useDocumentTeamPermissions = ({
  teamId,
  apolloClient,
}: UseLeaseDocumentPermissions): DocumentPermissions => {
  const { teamPermissions, ownedTeams, setTeamPermissions, setOwnedTeams, setAllTeams } = usePermissions();

  const allowedPermissionsForUser: TeamPermissionName[] = useMemo(
    () =>
      teamPermissions
        .filter(({ allowed }) => allowed)
        .filter(({ teamId: permissionTeamId }) => teamId === permissionTeamId)
        .map(({ permissionName }) => permissionName),
    [teamId, teamPermissions]
  );

  /**
   * Fetch permissions if we don't have any, then set them in the store
   */
  useEffect(() => {
    if (!teamId) {
      return;
    }

    getTeamPermissions(teamId!)(apolloClient).then(({ teamPermissions, ownedTeams, allTeams }: GetTeamPermissions) => {
      setTeamPermissions(teamPermissions);
      setOwnedTeams(ownedTeams);
      setAllTeams(allTeams);
    });
  }, [apolloClient, teamId, setTeamPermissions, setOwnedTeams, setAllTeams]);

  return useMemo(() => {
    /**
     * A user is an admin when they have the specified team ID in their owned teams list
     */
    const isTeamAdmin = teamId ? ownedTeams.includes(String(teamId)) : false;

    const hasViewPermission = allowedPermissionsForUser.includes(Permissions.View) || isTeamAdmin;
    const hasCommentPermission = allowedPermissionsForUser.includes(Permissions.Comment) || isTeamAdmin;
    const hasEditPermission = allowedPermissionsForUser.includes(Permissions.Edit) || isTeamAdmin;
    const hasInviteSendingPermission = allowedPermissionsForUser.includes(Permissions.Invite) || isTeamAdmin;
    const hasApprovePermission = allowedPermissionsForUser.includes(Permissions.Approve) || isTeamAdmin;
    const hasEditTemplatesPermission = allowedPermissionsForUser.includes(Permissions.EditTemplates) || isTeamAdmin;
    const hasManageTemplatesPermission = allowedPermissionsForUser.includes(Permissions.ManageTemplates) || isTeamAdmin;

    return {
      isAdmin: isTeamAdmin,
      hasViewPermission,
      hasCommentPermission,
      hasEditPermission,
      hasInviteSendingPermission,
      hasApprovePermission,
      hasEditTemplatesPermission,
      hasManageTemplatesPermission,
    };
  }, [ownedTeams, teamId, allowedPermissionsForUser]);
};
